type GraphQLDate = string | null;
type WithCreatedAt = { createdAt: GraphQLDate };
type UserInfo = WithCreatedAt & { company: WithCreatedAt };

const dateToTimestamp = (date: GraphQLDate) =>
  date ? Math.floor(new Date(date).valueOf() / 1000) : null;

const normalizeCreatedAtField = <T extends WithCreatedAt>(obj: T) => {
  const { createdAt, ...rest } = obj;
  return {
    ...rest,
    created_at: dateToTimestamp(obj.createdAt),
  };
};

// Normalize Segment identify payload to Intercom formats
// Inspired by https://github.com/segmentio/analytics.js-integrations/blob/c851772d7c652937c6c1e65beae137df09804246/integrations/intercom/lib/index.js#L108-L121
const normalizeUserInfo = <T extends UserInfo>(userInfo: T) => ({
  ...normalizeCreatedAtField(userInfo),
  company: normalizeCreatedAtField(userInfo.company),
});

const {
  __slab: { INTERCOM_APP_ID, CSP_NONCE },
} = window;

const DELAY_FOR_BETTER_FCP = 3000;

class IntercomFacade {
  private readonly baseSettings: Record<string, unknown> = {
    app_id: INTERCOM_APP_ID,
    hide_default_launcher: true,
  };

  hasError = false;

  constructor() {
    // Provided by Intercom
    // https://developers.intercom.com/installing-intercom/web/installation/
    const shimmedIntercom = (...args: unknown[]) => {
      shimmedIntercom.c(args);
    };
    shimmedIntercom.q = [] as unknown[][];
    shimmedIntercom.c = (args: unknown[]) => {
      shimmedIntercom.q.push(args);
    };

    window.Intercom = shimmedIntercom;
    window.Intercom('boot', this.baseSettings);

    setTimeout(() => {
      this.load();
    }, DELAY_FOR_BETTER_FCP);
  }

  identify(userId: string, userInfo: UserInfo, intercomHash: string) {
    window.Intercom?.('update', {
      ...this.baseSettings,
      user_id: userId,
      user_hash: intercomHash,
      ...normalizeUserInfo(userInfo),
    });
  }

  private load() {
    if (!INTERCOM_APP_ID) {
      this.hasError = true;
      return;
    }
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.nonce = CSP_NONCE;
    script.src = `https://widget.intercom.io/widget/${INTERCOM_APP_ID}`;
    document.body.appendChild(script);
    script.addEventListener('error', () => {
      this.hasError = true;
    });
  }
}

export default new IntercomFacade();
